import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {FilterService} from './filter.service';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {
  ChronieMaintenanceWindow,
  MaintenanceWindowInsuranceCompany,
  MaintenanceWindowListItem,
  MaintenanceWindowObjectListResponse,
  MaintenanceWindowPayload,
  MaintenanceWindowSaveResponse,
  MaintenanceWindowStatusResponse,
} from '../interface/chronie-maintenance-window';

@Injectable({
  providedIn: 'root'
})
export class ChronieMaintenanceWindowService {
  public readonly maintenanceWindowListSubject =
    new ReplaySubject<CountedAndPaginated<MaintenanceWindowListItem>>(1);

  private readonly baseUrl = '/rest/chronie/api/internal/maintenance-window';
  private readonly listUrl = '/rest/chronie/api/internal/maintenance-window-list';

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
  ) {
  }

  public sendForm(
    formData: MaintenanceWindowPayload,
    uuid?: string
  ): Observable<MaintenanceWindowSaveResponse> {
    if (!uuid) {
      return this.httpClient
        .post<MaintenanceWindowSaveResponse>(this.baseUrl, formData)
        .pipe(first());
    }

    return this.httpClient
      .put<MaintenanceWindowSaveResponse>(`${this.baseUrl}/${uuid}`, formData)
      .pipe(first());
  }

  public get(uuid: string): Observable<ChronieMaintenanceWindow> {
    return this.httpClient
      .get<ChronieMaintenanceWindow>(`${this.baseUrl}/${uuid}`)
      .pipe(first());
  }

  public setStatus(
    uuid: string,
    enabled: boolean
  ): Observable<MaintenanceWindowStatusResponse> {
    return this.httpClient
      .patch<MaintenanceWindowStatusResponse>(`${this.baseUrl}/${uuid}/status`, {enabled})
      .pipe(first());
  }

  public getInsuranceCompanies(): Observable<MaintenanceWindowInsuranceCompany[]> {
    return this.httpClient
      .get<MaintenanceWindowObjectListResponse<MaintenanceWindowInsuranceCompany>>(
        '/rest/chronie/api/internal/insurance-company'
      )
      .pipe(
        first(),
        map(response => response?.items || [])
      );
  }

  public loadList(
    page: number,
    pageSize: number,
    filters: object,
    subject?: Subject<CountedAndPaginated<MaintenanceWindowListItem>>,
  ): void {
    const url = `${this.listUrl}?page=${page + 1}&pageSize=${pageSize}&${this.filterService.parseFilters(filters)}`;

    this.httpClient.get<CountedAndPaginated<MaintenanceWindowListItem>>(url)
      .pipe(first())
      .subscribe((result: CountedAndPaginated<MaintenanceWindowListItem>) => {
        if (subject) {
          subject.next(result);
        } else {
          this.maintenanceWindowListSubject.next(result);
        }
      });
  }
}
